import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Row, Tag } from 'antd';
import moment from 'moment';

const tagStatusConfig = {
  LIVE: {
    backgroundColor: '#00DE5133',
    color: '#00DE51',
    text: 'Live',
    width: '50px',
  },
  FINISHED: {
    backgroundColor: '#b9b6b633',
    color: '#b9b6b6',
    text: 'Finished',
    width: '78px',
  },
  QUEUED: {
    backgroundColor: '#faad1433',
    color: '#faad14',
    text: 'Queued',
    width: '70px',
  },
  ATTRIBUTION: {
    backgroundColor: '#e1f0e5',
    color: '#9cada0',
    text: 'In Attr.',
    width: '70px',
  },
  DRAFT: {
    backgroundColor: 'lightgray',
    color: '#9cada0',
    text: 'Draft',
    width: '55px',
  },
  PENDING: {
    backgroundColor: '#1890ff',
    width: '70px',
    color: 'white',
    text: 'Pending',
  },
  PAUSED: {
    backgroundColor: 'orange',
    width: '70px',
    color: 'white',
    text: 'Paused',
  },
  DEMO: {
    backgroundColor: '#b9b6b633',
    color: '#b9b6b6',
    text: 'Demo',
    width: '60px',
  },
  LAUNCHING: {
    backgroundColor: 'orange',
    width: '70px',
    color: 'white',
    text: 'Launching',
  },
  APPROVED: {
    backgroundColor: '#faad1433',
    color: '#faad14',
    text: 'Approved',
    width: '85px',
  },
  REJECTED: {
    backgroundColor: '#ff4d4f33',
    color: '#ff4d4f',
    text: 'Rejected',
    width: '80px',
  },
  PENDING_CLIENT: {
    backgroundColor: '#faad1433',
    color: '#faad14',
    text: 'Pending Client',
    width: '120px',
  },
  ARCHIVED: {
    backgroundColor: '#b9b6b633',
    color: '#b9b6b6',
    text: 'Archived',
    width: '80px',
  },
  PAST: {
    backgroundColor: '#b9b6b633',
    color: '#b9b6b6',
    text: 'Past',
    width: '55px',
  },
};

export const TagWithConfig = ({ backgroundColor, color, text, width, status }) => (
  <Tag
    className={`status-badge-enhanced status-badge-${status}`}
    style={{
      width,
      backgroundColor,
      color,
      border: 0,
      borderRadius: '25px',
    }}
  >
    <Row justify="space-around" type="flex" align="middle">
      <div
        style={{
          width: '5px',
          height: '5px',
          borderRadius: '50px',
          backgroundColor: color,
        }}
      />
      {text}
    </Row>
  </Tag>
);

const getStatusOverride = (statusCampaign, checkStatus) => {
  if (statusCampaign === 'DRAFT' || checkStatus === 'DRAFT') return 'DRAFT';
  if (statusCampaign === 'PENDING' || checkStatus === 'PENDING') return 'PENDING';
  if (statusCampaign === 'LIVE_PENDING' || checkStatus === 'LIVE_PENDING') return 'PENDING';
  if (statusCampaign === 'PAUSED_PENDING' || checkStatus === 'PAUSED_PENDING') return 'PENDING';
  if (statusCampaign === 'PAUSED' || checkStatus === 'PAUSED') return 'PAUSED';
  if (statusCampaign === 'LAUNCHING_PAUSED' || checkStatus === 'LAUNCHING_PAUSED') return 'PAUSED';
  if (statusCampaign === 'LAUNCHING_LIVE' || checkStatus === 'LAUNCHING_LIVE') return 'LIVE';
  if (statusCampaign === 'DEMO' || checkStatus === 'DEMO') return 'DEMO';
  if (statusCampaign === 'PENDING_CLIENT' || checkStatus === 'PENDING_CLIENT')
    return 'PENDING_CLIENT';
  if (statusCampaign === 'APPROVED' || checkStatus === 'APPROVED') return 'APPROVED';
  if (statusCampaign === 'LIVE_APPROVED' || checkStatus === 'LIVE_APPROVED') return 'APPROVED';
  if (statusCampaign === 'PAUSED_APPROVED' || checkStatus === 'PAUSED_APPROVED') return 'APPROVED';
  if (statusCampaign === 'REJECTED' || checkStatus === 'REJECTED') return 'REJECTED';
  if (statusCampaign === 'ARCHIVED' || checkStatus === 'ARCHIVED') return 'ARCHIVED';
  if (statusCampaign === 'PAST' || checkStatus === 'PAST') return 'PAST';
  return null;
};

export const deriveStatus = (
  startDate,
  endDate,
  attributionWindow,
  statusCampaign,
  checkStatus
) => {
  /**
   * Dates are modified to be able to be checked with
   * the moment comparison functions.
   *
   * Setting the endDate to be the end of day and startDate
   * to be the start of day is needed to make it a "range"
   *
   * Also, setting up the current date in the middle will avoid the edge case
   * of checking it start of day or end of day.
   */
  const currentDateTime = moment().hours(12).minutes(0).seconds(0).milliseconds(0);
  startDate = startDate
    ? moment(startDate, moment.ISO_8601).hours(0).minutes(0).seconds(0).milliseconds(0)
    : null;
  endDate = endDate
    ? moment(endDate, moment.ISO_8601).hours(23).minutes(59).seconds(59).milliseconds(0)
    : null;

  // Check for status overrides first (except DEMO which needs date logic)
  const statusOverride = getStatusOverride(statusCampaign, checkStatus);
  if (statusOverride && statusOverride !== 'DEMO') {
    return statusOverride;
  }

  // Date-based logic with DEMO special handling
  if (
    currentDateTime.isBetween(startDate, endDate) ||
    checkStatus === 'DEMO' ||
    statusCampaign === 'DEMO'
  ) {
    if (statusCampaign === 'DEMO' || checkStatus === 'DEMO') return 'DEMO';
    return 'LIVE';
  }

  if (currentDateTime.isAfter(endDate)) {
    const attributionWindowEndDate = moment(endDate).add(attributionWindow, 'days');
    if (currentDateTime.isAfter(attributionWindowEndDate)) return 'FINISHED';
    return 'ATTRIBUTION';
  }

  if (currentDateTime.isBefore(startDate)) {
    return 'QUEUED';
  }

  return 'LIVE';
};

const StatusTag = ({ startDate, endDate, attributionWindow, statusCampaign, checkStatus }) => {
  const status = useMemo(
    () => deriveStatus(startDate, endDate, attributionWindow, statusCampaign, checkStatus),
    [startDate, endDate, attributionWindow, statusCampaign, checkStatus]
  );
  return <TagWithConfig {...tagStatusConfig[status]} status={status} />;
};

StatusTag.propTypes = {
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  attributionWindow: PropTypes.number,
};

export default StatusTag;
